import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import { 
  Buildings, MagnifyingGlass, Eye, CheckCircle, XCircle, 
  Users, Briefcase, Database, Archive, Trash, ArrowCounterClockwise,
  Funnel, ListBullets, Fire, CreditCard, ShieldWarning
} from "phosphor-react";
import TenantBackupsTab from "../../components/admin/TenantBackupsTab";
import TenantLifecycleTab from "../../components/admin/TenantLifecycleTab";
import SupportAccessModal from "../../components/admin/SupportAccessModal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Status filter options
const STATUS_FILTERS = [
  { value: "all", label: "All Tenants", icon: Buildings },
  { value: "active", label: "Active", icon: CheckCircle, color: "text-emerald-400" },
  { value: "deleted", label: "Deleted (Quarantine)", icon: Trash, color: "text-amber-400" },
  { value: "archived", label: "Archived", icon: Archive, color: "text-zinc-400" },
  { value: "purged", label: "Purged", icon: Fire, color: "text-red-400" },
];

// Status badge styling
function getStatusBadge(status) {
  const upperStatus = (status || '').toUpperCase();
  
  const styles = {
    active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    DELETED: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    DELETING_FAILED: "bg-red-500/20 text-red-400 border-red-500/30",
    ARCHIVED: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30",
    PURGED: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", // Legacy, treated as archived
    TRUE_PURGED: "bg-red-500/20 text-red-300 border-red-500/30",
    DELETING: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  };
  
  const labels = {
    active: "Active",
    DELETED: "Deleted",
    DELETING_FAILED: "Delete Failed",
    ARCHIVED: "Archived",
    PURGED: "Archived", // Legacy display
    TRUE_PURGED: "Purged",
    DELETING: "Deleting...",
  };
  
  return {
    className: styles[upperStatus] || styles.active,
    label: labels[upperStatus] || upperStatus || "Active"
  };
}

export default function AdminTenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [includePurged, setIncludePurged] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [supportAccessTenant, setSupportAccessTenant] = useState(null);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    all_including_purged: 0,
    active: 0,
    deleted: 0,
    archived: 0,
    purged: 0,
  });

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      // Build query params
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") {
        params.append("status", statusFilter);
      }
      // For "all" tab, pass include_purged param
      if (statusFilter === "all" && includePurged) {
        params.append("include_purged", "true");
      }
      
      const url = `${API}/admin/tenants${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setTenants(data.tenants || []);
        // Use server-provided counts if available
        if (data.counts) {
          setStatusCounts(data.counts);
        }
      }
    } catch (error) {
      console.error("Error fetching tenants:", error);
      toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, includePurged]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  // Filter tenants by search only (status filtering is now done server-side)
  useEffect(() => {
    let filtered = tenants;
    
    // Search filter (client-side for responsiveness)
    // Supports: company name, tenant_id, email, finix_buyer_id (biy_), finix_onboarding_form_id (obf_)
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(t => 
        t.company_name?.toLowerCase().includes(searchLower) ||
        t.tenant_id?.toLowerCase().includes(searchLower) ||
        t.owner?.email?.toLowerCase().includes(searchLower) ||
        t.finix_buyer_id?.toLowerCase().includes(searchLower) ||
        t.finix_onboarding_form_id?.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredTenants(filtered);
  }, [tenants, search]);

  const loadTenantDetail = async (tenant) => {
    try {
      setDetailLoading(true);
      const response = await fetch(`${API}/admin/tenants/${tenant.tenant_id}`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setSelectedTenant(data);
      }
    } catch (error) {
      console.error("Error loading tenant:", error);
      toast.error("Failed to load tenant");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleTenantUpdated = () => {
    fetchTenants();
    if (selectedTenant) {
      loadTenantDetail({ tenant_id: selectedTenant.tenant_id });
    }
  };

  // Get the count to display for "All" tab based on toggle
  const getAllCount = () => {
    return includePurged ? statusCounts.all_including_purged : statusCounts.all;
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-tenants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Buildings size={28} className="text-blue-400" />
            Tenant Management
          </h1>
          <p className="text-zinc-500 mt-1">
            Manage all tenants, exports, and lifecycle operations
          </p>
        </div>
        <Button
          onClick={() => navigate("/admin/tenants/lifecycle")}
          className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-600"
          data-testid="lifecycle-actions-btn"
        >
          <ListBullets size={18} className="mr-2" />
          Lifecycle Actions
        </Button>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 p-1 bg-zinc-800/50 rounded-lg border border-zinc-700">
        {STATUS_FILTERS.map((filter) => {
          const Icon = filter.icon;
          const isActive = statusFilter === filter.value;
          // Use getAllCount() for "all" tab, otherwise use statusCounts
          const count = filter.value === "all" ? getAllCount() : statusCounts[filter.value];
          
          return (
            <button
              key={filter.value}
              onClick={() => setStatusFilter(filter.value)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                isActive 
                  ? "bg-zinc-700 text-white" 
                  : "text-zinc-400 hover:text-white hover:bg-zinc-700/50"
              }`}
              data-testid={`filter-${filter.value}`}
            >
              <Icon size={16} className={filter.color} />
              <span>{filter.label}</span>
              <Badge variant="outline" className="bg-zinc-900 text-zinc-300 border-zinc-600 text-xs">
                {count || 0}
              </Badge>
            </button>
          );
        })}
        
        {/* Include purged toggle - only visible on All tab */}
        {statusFilter === "all" && statusCounts.purged > 0 && (
          <div className="flex items-center gap-2 ml-4 pl-4 border-l border-zinc-600">
            <Switch
              id="include-purged"
              checked={includePurged}
              onCheckedChange={setIncludePurged}
              className="data-[state=checked]:bg-red-600"
            />
            <label 
              htmlFor="include-purged" 
              className="text-sm text-zinc-400 cursor-pointer"
            >
              Include purged ({statusCounts.purged})
            </label>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
        <Input
          placeholder="Search by company, ID, email, biy_..., or obf_..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
          data-testid="tenant-search-input"
        />
      </div>

      {/* Tenants List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-20 bg-zinc-800 rounded animate-pulse" />
          ))}
        </div>
      ) : filteredTenants.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800/30 rounded-lg">
          <Funnel size={48} className="mx-auto mb-4 text-zinc-600" />
          <p className="text-zinc-400">No tenants match the current filter</p>
          {statusFilter !== "all" && (
            <Button
              variant="link"
              onClick={() => setStatusFilter("all")}
              className="text-blue-400 mt-2"
            >
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {filteredTenants.map((tenant) => {
            const statusInfo = getStatusBadge(tenant.status);
            
            return (
              <Card
                key={tenant.tenant_id}
                className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors cursor-pointer"
                onClick={() => navigate(`/admin/system?tab=billing-ops&tenant_id=${encodeURIComponent(tenant.tenant_id)}`)}
                data-testid={`tenant-row-${tenant.tenant_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-white">{tenant.company_name}</h3>
                        <Badge variant="outline" className={statusInfo.className}>
                          {statusInfo.label}
                        </Badge>
                        {tenant.subscription_tier && tenant.subscription_tier !== "free" && (
                          <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/30">
                            {tenant.subscription_tier}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-sm text-zinc-500">
                        <span className="font-mono">{tenant.tenant_id}</span>
                        {tenant.owner?.email && (
                          <span>• {tenant.owner.email}</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-center">
                        <div className="text-lg font-semibold text-white">{tenant.customer_count || 0}</div>
                        <div className="text-xs text-zinc-500">Customers</div>
                      </div>
                      <div className="text-center">
                        <div className="text-lg font-semibold text-white">{tenant.job_count || 0}</div>
                        <div className="text-xs text-zinc-500">Jobs</div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-amber-400"
                        title="Support Access"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSupportAccessTenant(tenant);
                        }}
                        data-testid={`support-access-${tenant.tenant_id}`}
                      >
                        <ShieldWarning size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-amber-400"
                        title="Billing Overview"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/admin/system?tab=billing-ops&tenant_id=${encodeURIComponent(tenant.tenant_id)}`);
                        }}
                        data-testid={`billing-overview-${tenant.tenant_id}`}
                      >
                        <CreditCard size={18} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-zinc-400 hover:text-white"
                        title="View Details"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadTenantDetail(tenant);
                        }}
                      >
                        <Eye size={18} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Support Access Modal */}
      <SupportAccessModal
        open={!!supportAccessTenant}
        onOpenChange={(open) => !open && setSupportAccessTenant(null)}
        tenant={supportAccessTenant}
      />

      {/* Tenant Detail Modal */}
      <Dialog open={!!selectedTenant} onOpenChange={(open) => !open && setSelectedTenant(null)}>
        <DialogContent className="max-w-4xl bg-zinc-900 border-zinc-700 text-white max-h-[90vh] overflow-y-auto">
          {selectedTenant && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <Buildings size={24} className="text-blue-400" />
                  {selectedTenant.company_name}
                  <Badge variant="outline" className={getStatusBadge(selectedTenant.status).className}>
                    {getStatusBadge(selectedTenant.status).label}
                  </Badge>
                </DialogTitle>
              </DialogHeader>
              
              <Tabs defaultValue="overview" className="mt-4">
                <TabsList className="bg-zinc-800 border-zinc-700">
                  <TabsTrigger value="overview" className="data-[state=active]:bg-zinc-700">
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="backups" className="data-[state=active]:bg-zinc-700">
                    <Database size={14} className="mr-1" />
                    Backups
                  </TabsTrigger>
                  <TabsTrigger value="lifecycle" className="data-[state=active]:bg-zinc-700">
                    <Archive size={14} className="mr-1" />
                    Data & Lifecycle
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="overview" className="mt-4 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-zinc-800 p-4 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-1">Tenant ID</p>
                      <p className="font-mono text-sm">{selectedTenant.tenant_id}</p>
                    </div>
                    <div className="bg-zinc-800 p-4 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-1">Subscription</p>
                      <p>{selectedTenant.subscription_tier || "Free"}</p>
                    </div>
                  </div>
                  
                  {selectedTenant.owner && (
                    <div className="bg-zinc-800 p-4 rounded-lg">
                      <p className="text-xs text-zinc-500 mb-2">Owner</p>
                      <div className="flex items-center gap-2">
                        <Users size={16} className="text-zinc-400" />
                        <span>{selectedTenant.owner.name}</span>
                        <span className="text-zinc-500">({selectedTenant.owner.email})</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-zinc-800 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold">{selectedTenant.customer_count || 0}</div>
                      <p className="text-xs text-zinc-500">Customers</p>
                    </div>
                    <div className="bg-zinc-800 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold">{selectedTenant.job_count || 0}</div>
                      <p className="text-xs text-zinc-500">Jobs</p>
                    </div>
                    <div className="bg-zinc-800 p-4 rounded-lg text-center">
                      <div className="text-2xl font-bold">{selectedTenant.technician_count || 0}</div>
                      <p className="text-xs text-zinc-500">Technicians</p>
                    </div>
                  </div>
                  
                  {/* Show deletion/archive info if applicable */}
                  {selectedTenant.status === "DELETED" && selectedTenant.deleted_at && (
                    <div className="bg-amber-500/10 border border-amber-500/30 p-4 rounded-lg">
                      <p className="text-amber-400 font-medium mb-1">Tenant Deleted</p>
                      <p className="text-sm text-zinc-400">
                        Deleted at: {new Date(selectedTenant.deleted_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-zinc-400 mt-2">
                        This tenant can be restored or archived permanently.
                      </p>
                    </div>
                  )}
                  
                  {selectedTenant.status === "ARCHIVED" && (
                    <div className="bg-zinc-500/10 border border-zinc-500/30 p-4 rounded-lg">
                      <p className="text-zinc-300 font-medium mb-1">Tenant Archived</p>
                      <p className="text-sm text-zinc-400">
                        Archived at: {new Date(selectedTenant.archived_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-zinc-400 mt-2">
                        This tenant is permanently disabled. Export data is retained for historical record.
                      </p>
                    </div>
                  )}
                </TabsContent>
                
                <TabsContent value="backups" className="mt-4">
                  <TenantBackupsTab 
                    tenantId={selectedTenant.tenant_id} 
                    companyName={selectedTenant.company_name}
                  />
                </TabsContent>
                
                <TabsContent value="lifecycle" className="mt-4">
                  <TenantLifecycleTab 
                    tenant={selectedTenant}
                    onUpdate={handleTenantUpdated}
                  />
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
